import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Select, Input, InputNumber, DatePicker, Modal, message, Tag, Dropdown, Radio, Tooltip, Popconfirm, Checkbox, Pagination, Spin } from 'antd';
import {
  PlusOutlined, DeleteOutlined, CopyOutlined, SwapOutlined,
  SearchOutlined, FilterOutlined, ThunderboltOutlined,
  SettingOutlined, ColumnHeightOutlined, EditOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../utils/api';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { useIsMobile } from '../hooks/useIsMobile';
import RangeFilter, { RANGE_PRESETS, GROWTH_PRESETS } from '../components/RangeFilter';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 可搜索的字段列表
const SEARCHABLE_FIELDS = [
  { label: '全部字段', value: 'all' },
  { label: '账号名称', value: 'account_name' },
  { label: '专属链接', value: 'exclusive_link' },
  { label: '备注', value: 'remark' },
  { label: '受理员工', value: 'handler' },
  { label: '小组', value: 'team' },
  { label: '群名', value: 'group_name' },
  { label: '对接人', value: 'contact_person' },
  { label: '涨粉方式', value: 'growth_method' },
  { label: '平台', value: 'platform' },
  { label: '文本1', value: 'text1' },
  { label: '文本2', value: 'text2' },
  { label: '文本3', value: 'text3' }
];

// 可批量修改的字段
const BATCH_EDITABLE_FIELDS = [
  { label: '主体', value: 'supplier_id', type: 'select', optionsKey: 'suppliers' },
  { label: '平台', value: 'platform', type: 'select', optionsKey: 'platforms' },
  { label: '受理员工', value: 'handler', type: 'input' },
  { label: '小组', value: 'team', type: 'select', optionsKey: 'teams' },
  { label: '期数', value: 'period', type: 'input' },
  { label: '群名', value: 'group_name', type: 'input' },
  { label: '下单时间', value: 'order_time', type: 'datetime' },
  { label: '对接人', value: 'contact_person', type: 'input' },
  { label: '涨粉方式', value: 'growth_method', type: 'select', optionsKey: 'growthMethods' },
  { label: '完结状态', value: 'status_id', type: 'select', optionsKey: 'statuses' },
  { label: '完成日期', value: 'complete_date', type: 'date' },
  { label: '备注', value: 'remark', type: 'textarea' },
  { label: '文本1', value: 'text1', type: 'input' },
  { label: '文本2', value: 'text2', type: 'input' },
  { label: '文本3', value: 'text3', type: 'input' },
  { label: '参考价格', value: 'reference_price', type: 'number' },
  { label: '实际价格', value: 'actual_price', type: 'number' }
];

function OrderList() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [user, setUser] = useState(null);

  // 筛选条件
  const [effectiveFansPreset, setEffectiveFansPreset] = useState('all');
  const [initialFansPreset, setInitialFansPreset] = useState('all');
  const [finalFansPreset, setFinalFansPreset] = useState('all');
  const [growthCountPreset, setGrowthCountPreset] = useState('all');
  const [filterExpanded, setFilterExpanded] = useState(false); // 手机端筛选区展开状态

  const [filters, setFilters] = useState({
    keyword: '',
    search_field: 'all',
    keywords: '',
    match_type: 'fuzzy',
    supplier_id: [],
    status_id: [],
    platform: undefined,
    growth_method: [],
    team: [],
    handler: undefined,
    effective_fans_min: undefined,
    effective_fans_max: undefined,
    initial_fans_min: undefined,
    initial_fans_max: undefined,
    final_fans_min: undefined,
    final_fans_max: undefined,
    growth_count_min: undefined,
    growth_count_max: undefined,
    dateRange: null,
    completeDateRange: null,
    updatedDateRange: null,
    sort_field: 'id',
    sort_order: 'DESC'
  });

  // 搜索模式
  const [searchMode, setSearchMode] = useState('simple'); // simple | batch

  // 快速导入弹窗
  const [quickImportVisible, setQuickImportVisible] = useState(false);
  const [quickImportText, setQuickImportText] = useState('');
  const [quickImporting, setQuickImporting] = useState(false);
  const [quickImportCheckType, setQuickImportCheckType] = useState('account');
  const [quickImportGrowthMethod, setQuickImportGrowthMethod] = useState(undefined);
  const [quickImportPlatform, setQuickImportPlatform] = useState(undefined);
  const [quickImportSupplier, setQuickImportSupplier] = useState(undefined);
  const [quickImportStatus, setQuickImportStatus] = useState(undefined);
  // 小组：不记忆，每次打开弹窗都留空
  const [quickImportTeam, setQuickImportTeam] = useState(undefined);

  // 独立查重弹窗
  const [checkDupVisible, setCheckDupVisible] = useState(false);
  const [checkDupText, setCheckDupText] = useState('');
  const [checkDupType, setCheckDupType] = useState('account');
  const [checkDupLoading, setCheckDupLoading] = useState(false);

  // 批量修改弹窗
  const [batchEditVisible, setBatchEditVisible] = useState(false);
  const [batchEditField, setBatchEditField] = useState(null);
  const [batchEditValue, setBatchEditValue] = useState(null);
  const [batchEditing, setBatchEditing] = useState(false);

  // 批量修改粉丝弹窗
  const [batchFansVisible, setBatchFansVisible] = useState(false);
  const [batchFansField, setBatchFansField] = useState('initial_fans');
  const [batchFansText, setBatchFansText] = useState('');
  const [batchFansLoading, setBatchFansLoading] = useState(false);

  // 授权码弹窗
  const [authCodeVisible, setAuthCodeVisible] = useState(false);
  const [authCodeRecord, setAuthCodeRecord] = useState(null);
  const [authCodeUrl, setAuthCodeUrl] = useState('');
  const [authCodeLoading, setAuthCodeLoading] = useState(false);

  // 已启用的接口平台列表
  const [enabledApiPlatforms, setEnabledApiPlatforms] = useState([]);

  // 下拉选项
  const [suppliers, setSuppliers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [teams, setTeams] = useState([]);
  const [growthMethods, setGrowthMethods] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [fieldConfig, setFieldConfig] = useState([]);
  const [fieldPermissions, setFieldPermissions] = useState({});

  // 筛选计数
  const [filterCounts, setFilterCounts] = useState({ statuses: [], growthMethods: [], teams: [], suppliers: [] });

  // 列配置
  const [columnConfigVisible, setColumnConfigVisible] = useState(false);
  const [columnOrder, setColumnOrder] = useState([]);

  // 双击编辑
  const [editingCell, setEditingCell] = useState(null); // { id, field }
  const [editingValue, setEditingValue] = useState('');
  const editingValueRef = useRef('');
  editingValueRef.current = editingValue;

  // 查重弹窗
  const [duplicateVisible, setDuplicateVisible] = useState(false);
  const [duplicateData, setDuplicateData] = useState([]);
  const [duplicateCheckType, setDuplicateCheckType] = useState('account'); // account | account_method
  const [pendingAccounts, setPendingAccounts] = useState([]);

  // 重复抖音号：Map<account_name, { type: 'account'|'account_method', methods: Set }>
  const [duplicateAccounts, setDuplicateAccounts] = useState(new Map());

  // 判断是否是有效URL链接
  const isValidUrl = (text) => {
    if (!text || typeof text !== 'string') return false;
    return /^https?:\/\/.+/i.test(text.trim());
  };

  // 所有状态（状态权限是限制修改，不限制查看）
  const visibleStatuses = statuses;

  // 选项文案悬停显示完整内容（长名称被截断时可查看全称）
  const withTitle = (options) => options.map(o => ({ ...o, title: o.label }));

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      const u = JSON.parse(stored);
      setUser(u);
      loadFieldConfig(u);
      loadFieldPermissions(u);
    }
    loadSuppliers();
    loadStatuses();
    loadTeams();
    loadGrowthMethods();
    loadPlatforms();
    loadColumnConfig();
    loadFilterCounts();

    // 加载快速导入记忆
    const savedQuickImport = localStorage.getItem('quickImportDefaults');
    if (savedQuickImport) {
      try {
        const defaults = JSON.parse(savedQuickImport);
        if (defaults.growth_method) setQuickImportGrowthMethod(defaults.growth_method);
        if (defaults.platform) setQuickImportPlatform(defaults.platform);
        if (defaults.supplier_id) setQuickImportSupplier(defaults.supplier_id);
        if (defaults.status_id) setQuickImportStatus(defaults.status_id);
      } catch (e) {}
    }

    // 监听用户权限更新
    const handleUserUpdated = () => {
      const s = localStorage.getItem('user');
      if (s) {
        const u = JSON.parse(s);
        setUser(u);
        loadFieldPermissions(u);
      }
    };
    window.addEventListener('userUpdated', handleUserUpdated);
    return () => window.removeEventListener('userUpdated', handleUserUpdated);
  }, []);

  useEffect(() => {
    loadData();
    // 筛选条件变化时重新加载计数（防抖）
    const timer = setTimeout(() => { loadFilterCounts(filters); }, 300);
    return () => clearTimeout(timer);
  }, [page, pageSize, filters]);

  const loadSuppliers = async () => {
    try {
      const res = await api.get('/suppliers/all');
      if (res.code === 200) setSuppliers(res.data);
    } catch (err) {}
  };

  const loadStatuses = async () => {
    try {
      const res = await api.get('/statuses');
      if (res.code === 200) setStatuses(res.data);
    } catch (err) {}
  };

  const loadTeams = async () => {
    try {
      const res = await api.get('/teams');
      if (res.code === 200) setTeams(res.data);
    } catch (err) {}
  };

  const loadGrowthMethods = async () => {
    try {
      const res = await api.get('/growth-methods');
      if (res.code === 200) setGrowthMethods(res.data);
    } catch (err) {}
  };

  const loadPlatforms = async () => {
    try {
      const res = await api.get('/platforms');
      if (res.code === 200) setPlatforms(res.data);
    } catch (err) {}
  };

  const loadFilterCounts = async (currentFilters) => {
    try {
      const f = currentFilters || filters;
      const params = {};
      if (f.supplier_id && f.supplier_id.length > 0) params.supplier_id = f.supplier_id.join(',');
      if (f.status_id && f.status_id.length > 0) params.status_id = f.status_id.join(',');
      if (f.platform) params.platform = f.platform;
      if (f.growth_method && f.growth_method.length > 0) params.growth_method = f.growth_method.join(',');
      if (f.team && f.team.length > 0) params.team = f.team.join(',');
      if (f.handler) params.handler = f.handler;
      if (f.dateRange && f.dateRange[0]) {
        params.start_date = f.dateRange[0].format('YYYY-MM-DD');
        params.end_date = f.dateRange[1].format('YYYY-MM-DD');
      }
      if (f.completeDateRange && f.completeDateRange[0]) {
        params.complete_date_start = f.completeDateRange[0].format('YYYY-MM-DD');
        params.complete_date_end = f.completeDateRange[1].format('YYYY-MM-DD');
      }
      if (f.updatedDateRange && f.updatedDateRange[0]) {
        params.updated_date_start = f.updatedDateRange[0].format('YYYY-MM-DD');
        params.updated_date_end = f.updatedDateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/orders/filter-counts', { params });
      if (res.code === 200) setFilterCounts(res.data);
    } catch (err) {}
  };

  const loadFieldPermissions = async (u) => {
    try {
      const endpoint = u.role === 'supplier' ? '/suppliers/my/fields' : '/users/my/fields';
      const res = await api.get(endpoint);
      if (res.code === 200) {
        const permMap = {};
        res.data.forEach(f => {
          permMap[f.field_name] = { visible: f.is_visible === true || f.is_visible === 1, editable: f.is_editable === true || f.is_editable === 1 };
        });
        setFieldPermissions(permMap);
      }
    } catch (err) {}
  };

  const loadFieldConfig = async (u) => {
    if (u.role === 'supplier' && u.supplier_id) {
      try {
        const res = await api.get(`/suppliers/${u.supplier_id}/fields`);
        if (res.code === 200) {
          setFieldConfig(res.data);
        }
      } catch (err) {}
    }
  };

  // 加载列配置
  const COLUMN_CONFIG_VERSION = 2;
  const loadColumnConfig = () => {
    try {
      const version = parseInt(localStorage.getItem('order_column_config_version') || '0');
      if (version < COLUMN_CONFIG_VERSION) {
        localStorage.removeItem('order_column_config');
        localStorage.setItem('order_column_config_version', COLUMN_CONFIG_VERSION);
        return;
      }
      const saved = localStorage.getItem('order_column_config');
      if (saved) {
        setColumnOrder(JSON.parse(saved));
      }
    } catch (err) {}
  };

  // 保存列配置
  const saveColumnConfig = (config) => {
    localStorage.setItem('order_column_config', JSON.stringify(config));
    setColumnOrder(config);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setSelectedRowKeys([]);
    try {
      const params = {
        page,
        pageSize,
        sort_field: filters.sort_field,
        sort_order: filters.sort_order
      };

      if (searchMode === 'batch' && filters.keywords) {
        params.keywords = filters.keywords;
        params.search_field = filters.search_field;
        params.match_type = filters.match_type;
      } else if (filters.keyword) {
        if (filters.search_field === 'all') {
          params.keyword = filters.keyword;
        } else {
          params.keywords = filters.keyword;
          params.search_field = filters.search_field;
          params.match_type = filters.match_type;
        }
      }

      if (filters.supplier_id && filters.supplier_id.length > 0) params.supplier_id = filters.supplier_id.join(',');
      if (filters.status_id && filters.status_id.length > 0) params.status_id = filters.status_id.join(',');
      if (filters.platform) params.platform = filters.platform;
      if (filters.growth_method && filters.growth_method.length > 0) params.growth_method = filters.growth_method.join(',');
      if (filters.team && filters.team.length > 0) params.team = filters.team.join(',');
      if (filters.handler) params.handler = filters.handler;
      if (filters.effective_fans_min !== undefined && filters.effective_fans_min !== null) {
        params.effective_fans_min = filters.effective_fans_min;
      }
      if (filters.effective_fans_max !== undefined && filters.effective_fans_max !== null) {
        params.effective_fans_max = filters.effective_fans_max;
      }
      if (filters.initial_fans_min !== undefined && filters.initial_fans_min !== null) params.initial_fans_min = filters.initial_fans_min;
      if (filters.initial_fans_max !== undefined && filters.initial_fans_max !== null) params.initial_fans_max = filters.initial_fans_max;
      if (filters.final_fans_min !== undefined && filters.final_fans_min !== null) params.final_fans_min = filters.final_fans_min;
      if (filters.final_fans_max !== undefined && filters.final_fans_max !== null) params.final_fans_max = filters.final_fans_max;
      if (filters.growth_count_min !== undefined && filters.growth_count_min !== null) params.growth_count_min = filters.growth_count_min;
      if (filters.growth_count_max !== undefined && filters.growth_count_max !== null) params.growth_count_max = filters.growth_count_max;
      if (filters.dateRange && filters.dateRange[0]) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
      }
      if (filters.completeDateRange && filters.completeDateRange[0]) {
        params.complete_date_start = filters.completeDateRange[0].format('YYYY-MM-DD');
        params.complete_date_end = filters.completeDateRange[1].format('YYYY-MM-DD');
      }
      if (filters.updatedDateRange && filters.updatedDateRange[0]) {
        params.updated_date_start = filters.updatedDateRange[0].format('YYYY-MM-DD');
        params.updated_date_end = filters.updatedDateRange[1].format('YYYY-MM-DD');
      }

      const res = await api.get('/orders', { params });
      if (res.code === 200) {
        setData(res.data.list);
        setTotal(res.data.total);
      }
    } catch (err) {}
    setLoading(false);
  }, [page, pageSize, filters, searchMode]);

  // 加载启用的接口平台（轻量接口，不含密钥）
  const loadEnabledApiPlatforms = async () => {
    try {
      const res = await api.get('/api-configs/enabled-platforms');
      if (res.code === 200) {
        setEnabledApiPlatforms([...new Set(res.data || [])]);
      }
    } catch (err) {}
  };
  useEffect(() => { loadEnabledApiPlatforms(); }, []);

  // 表格排序变化
  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.field) {
      setFilters(prev => ({
        ...prev,
        sort_field: sorter.field,
        sort_order: sorter.order === 'ascend' ? 'ASC' : 'DESC'
      }));
    }
  };

  // 双击编辑（字段权限为唯一判断依据）
  // fieldPermissions 非空时严格执行；为空时（未配置）默认允许
  const hasFieldPermConfig = Object.keys(fieldPermissions).length > 0;
  const handleCellDoubleClick = (record, field) => {
    if (isStatusRestricted(record)) return;
    if (!isSuperAdmin) {
      if (!canEdit) return;
      if (isAdmin) {
        if (hasFieldPermConfig) {
          const editPerm = fieldPermissions[field];
          if (!editPerm || (editPerm.editable !== true && editPerm.editable !== 1)) return;
        }
      } else {
        if (fieldConfig.length > 0) {
          const config = fieldConfig.find(f => f.field_name === field);
          if (!config || (config.is_editable !== true && config.is_editable !== 1)) return;
        }
      }
    }
    const val = record[field] ?? '';
    editingValueRef.current = val;
    setEditingCell({ id: record.id, field });
    setEditingValue(val);
  };

  const handleSaveEdit = async (id, field) => {
    try {
      let confirmDuplicate = false;
      // 修改抖音号：检测是否已存在，提示确认
      if (field === 'account_name') {
        const newName = String(editingValueRef.current || '').trim();
        if (newName) {
          const dupRes = await api.get('/orders/check-account', { params: { account: newName, exclude_id: id } });
          const dups = (dupRes.data && dupRes.data.duplicates) || [];
          if (dups.length > 0) {
            const ok = await new Promise(resolve => {
              Modal.confirm({
                title: '抖音号已存在',
                content: `抖音号 ${newName} 在系统中已存在 ${dups.length} 条订单，是否继续修改？`,
                okText: '继续修改',
                cancelText: '取消',
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
              });
            });
            if (!ok) return;
            confirmDuplicate = true;
          }
        }
      }
      const res = await api.patch(`/orders/${id}`, { field, value: editingValueRef.current, confirm_duplicate: confirmDuplicate });
      if (res.code === 200) {
        message.success('保存成功');
        setEditingCell(null);
        loadData();
      } else {
        message.error(res.message || '保存失败');
      }
    } catch (err) {
      // 错误已在 api 拦截器中显示
    }
  };

  const handleCancelEdit = () => {
    editingValueRef.current = '';
    setEditingCell(null);
    setEditingValue('');
  };

  // 计算重复抖音号（红色）和账号+涨粉方式重复（紫色）
  useEffect(() => {
    const accountCounts = {};
    const comboCounts = {};
    data.forEach(r => {
      if (r.account_name) {
        accountCounts[r.account_name] = (accountCounts[r.account_name] || 0) + 1;
        const comboKey = `${r.account_name}|||${r.growth_method || ''}`;
        comboCounts[comboKey] = (comboCounts[comboKey] || 0) + 1;
      }
    });
    // 找出账号+涨粉方式重复的组合
    const purpleCombos = new Set(Object.entries(comboCounts).filter(([, c]) => c > 1).map(([k]) => k));
    // 构建Map：优先紫色
    const dupMap = new Map();
    Object.entries(accountCounts).filter(([, c]) => c > 1).forEach(([account]) => {
      const methods = new Set();
      data.forEach(r => {
        if (r.account_name === account) {
          const comboKey = `${account}|||${r.growth_method || ''}`;
          if (purpleCombos.has(comboKey)) methods.add(r.growth_method || '');
        }
      });
      dupMap.set(account, {
        type: methods.size > 0 ? 'account_method' : 'account',
        methods
      });
    });
    setDuplicateAccounts(dupMap);
  }, [data]);

  // 查重
  const handleCheckDuplicate = async (accounts, checkType) => {
    try {
      const res = await api.get('/orders/check-duplicate', {
        params: { accounts: accounts.join(','), check_type: checkType }
      });
      if (res.code === 200) {
        setDuplicateData(res.data);
        setDuplicateCheckType(checkType);
        return res.data;
      }
    } catch (err) {}
    return [];
  };

  // 批量删除
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的订单');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定删除选中的 ${selectedRowKeys.length} 条订单吗？`,
      onOk: async () => {
        try {
          for (const id of selectedRowKeys) {
            await api.delete(`/orders/${id}`);
          }
          message.success('删除成功');
          setSelectedRowKeys([]);
          loadData();
        } catch (err) {}
      }
    });
  };

  // 批量复制抖音号到剪切板
  const handleBatchCopy = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要复制的订单');
      return;
    }
    // 获取选中行的抖音号
    const selectedRows = data.filter(row => selectedRowKeys.includes(row.id));
    const accountNames = selectedRows
      .map(row => row.account_name)
      .filter(name => name)
      .join('\n');

    if (!accountNames) {
      message.warning('选中的订单没有抖音号');
      return;
    }

    try {
      await navigator.clipboard.writeText(accountNames);
      message.success(`已复制 ${selectedRows.filter(r => r.account_name).length} 个抖音号到剪切板`);
    } catch (err) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = accountNames;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      message.success(`已复制 ${selectedRows.filter(r => r.account_name).length} 个抖音号到剪切板`);
    }
  };

  // 导出Excel
  const handleExport = async (mode) => {
    try {
      const params = {};
      // mode: 'selected' | 'filtered' | 'all'
      if (mode === 'selected' && selectedRowKeys.length > 0) {
        params.ids = selectedRowKeys.join(',');
      } else if (mode === 'filtered') {
        // 传递当前筛选条件
        if (searchMode === 'batch' && filters.keywords) {
          params.keywords = filters.keywords;
          params.search_field = filters.search_field;
          params.match_type = filters.match_type;
        } else if (filters.keyword) {
          params.keyword = filters.keyword;
          params.search_field = filters.search_field;
          params.match_type = filters.match_type;
        }
        if (filters.supplier_id?.length > 0) params.supplier_id = filters.supplier_id.join(',');
        if (filters.status_id?.length > 0) params.status_id = filters.status_id.join(',');
        if (filters.platform) params.platform = filters.platform;
        if (filters.growth_method?.length > 0) params.growth_method = filters.growth_method.join(',');
        if (filters.team?.length > 0) params.team = filters.team.join(',');
        if (filters.handler) params.handler = filters.handler;
        if (filters.effective_fans_min !== undefined && filters.effective_fans_min !== null) params.effective_fans_min = filters.effective_fans_min;
        if (filters.effective_fans_max !== undefined && filters.effective_fans_max !== null) params.effective_fans_max = filters.effective_fans_max;
        if (filters.initial_fans_min !== undefined && filters.initial_fans_min !== null) params.initial_fans_min = filters.initial_fans_min;
        if (filters.initial_fans_max !== undefined && filters.initial_fans_max !== null) params.initial_fans_max = filters.initial_fans_max;
        if (filters.final_fans_min !== undefined && filters.final_fans_min !== null) params.final_fans_min = filters.final_fans_min;
        if (filters.final_fans_max !== undefined && filters.final_fans_max !== null) params.final_fans_max = filters.final_fans_max;
        if (filters.growth_count_min !== undefined && filters.growth_count_min !== null) params.growth_count_min = filters.growth_count_min;
        if (filters.growth_count_max !== undefined && filters.growth_count_max !== null) params.growth_count_max = filters.growth_count_max;
        if (filters.dateRange?.[0]) params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
        if (filters.dateRange?.[1]) params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
        if (filters.completeDateRange?.[0]) params.complete_date_start = filters.completeDateRange[0].format('YYYY-MM-DD');
        if (filters.completeDateRange?.[1]) params.complete_date_end = filters.completeDateRange[1].format('YYYY-MM-DD');
        if (filters.updatedDateRange?.[0]) params.updated_date_start = filters.updatedDateRange[0].format('YYYY-MM-DD');
        if (filters.updatedDateRange?.[1]) params.updated_date_end = filters.updatedDateRange[1].format('YYYY-MM-DD');
      }
      const res = await api.get('/orders/export', {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `orders_${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      if (mode === 'selected') message.success(`导出 ${selectedRowKeys.length} 条订单`);
      else if (mode === 'filtered') message.success('导出筛选数据');
      else message.success('导出全部订单');
    } catch (err) {
      message.error('导出失败');
    }
  };

  // 批量分配
  const handleBatchAssign = async (supplierId) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要分配的订单');
      return;
    }
    try {
      const res = await api.post('/orders/batch/assign', {
        ids: selectedRowKeys,
        supplier_id: supplierId
      });
      if (res.code === 200) {
        if (res.data?.skipped > 0) {
          message.warning(res.message);
        } else {
          message.success(res.message || '分配成功');
        }
        setSelectedRowKeys([]);
        loadData();
      }
    } catch (err) {}
  };

  // 批量修改状态
  const handleBatchStatus = async (statusId) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要修改的订单');
      return;
    }
    try {
      const res = await api.put('/orders/batch/status', {
        ids: selectedRowKeys,
        status_id: statusId
      });
      if (res.code === 200) {
        if (res.data?.skipped > 0) {
          message.warning(res.message);
        } else {
          message.success(res.message || '状态更新成功');
        }
        setSelectedRowKeys([]);
        loadData();
        loadFilterCounts();
      }
    } catch (err) {}
  };

  // 批量修改字段
  const handleBatchEdit = async () => {
    if (!batchEditField || batchEditValue === undefined || batchEditValue === null) {
      message.warning('请选择字段和值');
      return;
    }

    setBatchEditing(true);
    try {
      const res = await api.put('/orders/batch/field', {
        ids: selectedRowKeys,
        field: batchEditField,
        value: batchEditValue
      });
      if (res.code === 200) {
        if (res.data?.skipped > 0) {
          message.warning(res.message);
        } else {
          message.success(res.message || '批量更新成功');
        }
        setBatchEditVisible(false);
        setBatchEditField(null);
        setBatchEditValue(null);
        setSelectedRowKeys([]);
        loadData();
        loadFilterCounts();
      }
    } catch (err) {}
    setBatchEditing(false);
  };

  // 批量修改粉丝数
  const handleBatchFans = async () => {
    if (!batchFansText.trim()) {
      message.warning('请输入粉丝数据');
      return;
    }
    setBatchFansLoading(true);
    try {
      const res = await api.put('/orders/batch/fans', {
        ids: selectedRowKeys,
        field: batchFansField,
        lines: batchFansText
      });
      if (res.code === 200) {
        if (res.data?.skipped > 0) {
          message.warning(res.message);
        } else {
          message.success(res.message || '批量更新成功');
        }
        setBatchFansVisible(false);
        setBatchFansText('');
        setSelectedRowKeys([]);
        loadData();
        loadFilterCounts();
      }
    } catch (err) {}
    setBatchFansLoading(false);
  };

  // 快速导入 - 获取额外字段并保存记忆
  const getQuickImportExtras = () => {
    const extras = {};
    if (quickImportGrowthMethod) extras.growth_method = quickImportGrowthMethod;
    if (quickImportPlatform) extras.platform = quickImportPlatform;
    if (quickImportSupplier) extras.supplier_id = quickImportSupplier;
    if (quickImportStatus) extras.status_id = quickImportStatus;
    if (quickImportTeam) extras.team = quickImportTeam;
    // 保存记忆（小组不记忆，每次打开留空）
    localStorage.setItem('quickImportDefaults', JSON.stringify({
      growth_method: quickImportGrowthMethod || '',
      platform: quickImportPlatform || '',
      supplier_id: quickImportSupplier || '',
      status_id: quickImportStatus || ''
    }));
    return extras;
  };

  // 快速导入
  const handleQuickImport = async () => {
    if (!quickImportTeam) {
      message.warning('请选择小组');
      return;
    }
    if (!quickImportText.trim()) {
      message.warning('请输入抖音号');
      return;
    }

    const accounts = quickImportText.split('\n').map(s => s.trim()).filter(s => s);
    if (accounts.length === 0) {
      message.warning('请输入抖音号');
      return;
    }

    // 查重
    const duplicates = await handleCheckDuplicate(accounts, quickImportCheckType);
    if (duplicates.length > 0) {
      const dupAccounts = [...new Set(duplicates.map(d => d.account_name))];
      setPendingAccounts(accounts);
      setDuplicateVisible(true);
      message.warning(`发现 ${dupAccounts.length} 个重复抖音号，请查看查重结果`);
      return;
    }

    setQuickImporting(true);
    try {
      const extras = getQuickImportExtras();
      const res = await api.post('/orders/batch/quick-add', { accounts, ...extras });
      if (res.code === 200) {
        message.success(res.message);
        setQuickImportVisible(false);
        setQuickImportText('');
        loadData();
      }
    } catch (err) {}
    setQuickImporting(false);
  };

  // 强制导入（忽略重复）
  const handleForceImport = async () => {
    if (!quickImportTeam) {
      message.warning('请选择小组');
      return;
    }
    setQuickImporting(true);
    try {
      const extras = getQuickImportExtras();
      const res = await api.post('/orders/batch/quick-add', { accounts: pendingAccounts, ...extras });
      if (res.code === 200) {
        message.success(res.message);
        setQuickImportVisible(false);
        setQuickImportText('');
        setDuplicateVisible(false);
        setPendingAccounts([]);
        loadData();
      }
    } catch (err) {}
    setQuickImporting(false);
  };

  // 去重后导入
  const handleDedupImport = async () => {
    if (!quickImportTeam) {
      message.warning('请选择小组');
      return;
    }
    const duplicates = await handleCheckDuplicate(pendingAccounts, 'account');
    const dupSet = new Set(duplicates.map(d => d.account_name));
    const uniqueAccounts = pendingAccounts.filter(a => !dupSet.has(a));

    if (uniqueAccounts.length === 0) {
      message.warning('去除重复后没有可导入的数据');
      return;
    }

    setQuickImporting(true);
    try {
      const extras = getQuickImportExtras();
      const res = await api.post('/orders/batch/quick-add', { accounts: uniqueAccounts, ...extras });
      if (res.code === 200) {
        message.success(res.message);
        setQuickImportVisible(false);
        setQuickImportText('');
        setDuplicateVisible(false);
        setPendingAccounts([]);
        loadData();
      }
    } catch (err) {}
    setQuickImporting(false);
  };

  // 独立查重
  const handleStandaloneCheckDup = async () => {
    if (!checkDupText.trim()) {
      message.warning('请输入抖音号');
      return;
    }
    const accounts = checkDupText.split('\n').map(s => s.trim()).filter(s => s);
    if (accounts.length === 0) {
      message.warning('请输入抖音号');
      return;
    }
    setCheckDupLoading(true);
    const duplicates = await handleCheckDuplicate(accounts, checkDupType);
    setCheckDupLoading(false);
    if (duplicates.length === 0) {
      message.success('未发现重复数据');
    } else {
      message.warning(`发现 ${duplicates.length} 条重复记录`);
    }
  };

  // 获取批量修改字段的选项
  const getFieldOptions = (fieldDef) => {
    if (fieldDef.options) return fieldDef.options;
    if (fieldDef.optionsKey === 'suppliers') return suppliers.map(s => ({ label: s.name, value: s.id }));
    if (fieldDef.optionsKey === 'teams') return teams.map(t => ({ label: t.name, value: t.name }));
    if (fieldDef.optionsKey === 'growthMethods') return growthMethods.map(g => ({ label: g.name, value: g.name }));
    if (fieldDef.optionsKey === 'statuses') return selectableStatuses.map(s => ({ label: s.name, value: s.id }));
    if (fieldDef.optionsKey === 'platforms') return platforms.map(p => ({ label: p.name, value: p.name }));
    return [];
  };

  // 渲染批量修改的值输入控件
  const renderBatchEditInput = () => {
    if (!batchEditField) return null;
    const fieldDef = BATCH_EDITABLE_FIELDS.find(f => f.value === batchEditField);
    if (!fieldDef) return null;

    switch (fieldDef.type) {
      case 'select':
        return (
          <Select
            key={batchEditField}
            popupMatchSelectWidth={false}
            style={{ width: '100%' }}
            placeholder="请选择"
            value={batchEditValue}
            onChange={setBatchEditValue}
            options={withTitle(getFieldOptions(fieldDef))}
            allowClear
          />
        );
      case 'datetime':
        return (
          <DatePicker
            key={batchEditField}
            showTime
            style={{ width: '100%' }}
            value={batchEditValue ? dayjs(batchEditValue) : null}
            onChange={(date) => setBatchEditValue(date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
          />
        );
      case 'date':
        return (
          <DatePicker
            key={batchEditField}
            style={{ width: '100%' }}
            value={batchEditValue ? dayjs(batchEditValue) : null}
            onChange={(date) => setBatchEditValue(date ? date.format('YYYY-MM-DD') : null)}
          />
        );
      case 'number':
        return (
          <Input
            key={batchEditField}
            type="number"
            style={{ width: '100%' }}
            value={batchEditValue}
            onChange={e => setBatchEditValue(e.target.value)}
          />
        );
      case 'textarea':
        return (
          <TextArea
            key={batchEditField}
            rows={3}
            value={batchEditValue}
            onChange={e => setBatchEditValue(e.target.value)}
          />
        );
      default:
        return (
          <Input
            key={batchEditField}
            value={batchEditValue}
            onChange={e => setBatchEditValue(e.target.value)}
          />
        );
    }
  };

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.username === 'admin';
  const { canEdit } = useMenuPermission();

  // 判断用户是否有新增/删除权限（同时检查菜单可编辑权限）
  const canAdd = canEdit && (isSuperAdmin || user?.allow_add !== 0);
  const canDelete = canEdit && (isSuperAdmin || user?.allow_delete !== 0);
  const canImport = canEdit && (isSuperAdmin || user?.allow_import !== 0);
  const canQuickAdd = canEdit && (isSuperAdmin || user?.allow_quick_add !== 0);
  const canBatchAssign = canEdit && (isSuperAdmin || user?.allow_batch_assign !== 0);
  const canBatchEdit = canEdit && (isSuperAdmin || user?.allow_batch_edit !== 0);

  // 菜单可见性（批量导入类按钮需同时具备菜单权限）
  const menuVisible = (key) => isSuperAdmin ||
    (user?.menu_permissions || []).some(p => p.key === key && p.is_visible === 1);
  const canImportMenu = menuVisible('/orders/import');

  // 数据权限限制修改的状态：订单处于该状态后即锁定，禁止再改出；可正常改入
  const restrictedStatusIds = (user?.data_permissions?.statuses || []).map(Number);
  const isStatusRestricted = (record) => {
    if (isSuperAdmin) return false;
    if (record.status_is_locked === 1) return true;
    return record.status_id != null && restrictedStatusIds.includes(Number(record.status_id));
  };
  // 可作为目标的可选状态：全部状态都可改入（含锁定/受限状态）；
  // 限制只作用于「改出」——订单一旦处于锁定或受限状态，就不能再改成其他状态
  const selectableStatuses = statuses;

  // 主体可批量编辑的字段（根据权限过滤）
  const getBatchEditableFields = () => {
    if (isSuperAdmin) return BATCH_EDITABLE_FIELDS;
    if (isAdmin) {
      // 未配置字段权限时允许所有字段
      if (!hasFieldPermConfig) return BATCH_EDITABLE_FIELDS;
      return BATCH_EDITABLE_FIELDS.filter(f => {
        const perm = fieldPermissions[f.value];
        return perm && (perm.editable === true || perm.editable === 1);
      });
    }
    // 主体用户
    return BATCH_EDITABLE_FIELDS.filter(f => {
      if (f.value === 'supplier_id') return false;
      // 未配置字段权限时允许所有字段
      if (fieldConfig.length === 0) return true;
      const perm = fieldConfig.find(p => p.field_name === f.value);
      return perm && (perm.is_editable === true || perm.is_editable === 1);
    });
  };

  // 判断字段是否可见
  const isFieldVisible = (fieldName) => {
    if (isSuperAdmin) return true;
    if (isAdmin) {
      if (!hasFieldPermConfig) return true;
      const perm = fieldPermissions[fieldName];
      return perm && (perm.visible === true || perm.visible === 1);
    }
    if (fieldConfig.length === 0) return true;
    const config = fieldConfig.find(f => f.field_name === fieldName);
    return config ? (config.is_visible === true || config.is_visible === 1) : false;
  };

  // 判断字段是否可编辑
  // 传入 record 时，若该订单处于锁定/权限限制状态，则状态字段也不可编辑
  const isFieldEditable = (fieldName, record) => {
    if (isSuperAdmin) return true;
    if (fieldName === 'status_id' && record && isStatusRestricted(record)) return false;
    if (isAdmin) {
      if (!hasFieldPermConfig) return true;
      const perm = fieldPermissions[fieldName];
      return perm && (perm.editable === true || perm.editable === 1);
    }
    if (fieldConfig.length === 0) return true;
    const config = fieldConfig.find(f => f.field_name === fieldName);
    return config ? (config.is_editable === true || config.is_editable === 1) : false;
  };

  // 可双击编辑的字段
  const EDITABLE_FIELDS = ['handler', 'team', 'period', 'group_name', 'contact_person', 'growth_method', 'remark', 'text1', 'text2', 'text3', 'platform', 'filter_use', 'actual_price', 'reference_price', 'initial_fans', 'final_fans', 'effective_fans', 'exclusive_link'];

  // 渲染可编辑单元格
  const renderEditableCell = (field, record, renderFn) => {
    const isEditing = editingCell?.id === record.id && editingCell?.field === field;
    if (isEditing) {
      const isSelect = ['team', 'growth_method', 'platform'].includes(field);
      const isNumber = ['actual_price', 'reference_price', 'initial_fans', 'final_fans', 'effective_fans'].includes(field);
      if (isSelect) {
        const options = field === 'team' ? teams : field === 'growth_method' ? growthMethods : platforms;
        const handleChange = async (v) => {
          editingValueRef.current = v;
          setEditingValue(v);
          if (field === 'growth_method' && v) {
            const method = growthMethods.find(g => g.name === v);
            if (method) {
              // 涨粉方式：price=参考价格，reference_price=实际价格
              if (method.reference_price && !record.actual_price) {
                await api.patch(`/orders/${record.id}`, { field: 'actual_price', value: method.reference_price });
              }
            }
          }
        };
        return (
          <Select size="small" style={{ width: '100%' }} autoFocus
            popupMatchSelectWidth={false}
            value={editingValue || undefined}
            onChange={handleChange}
            onBlur={() => handleSaveEdit(record.id, field)}
            onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit(record.id, field); if (e.key === 'Escape') handleCancelEdit(); }}
            options={withTitle(options.map(o => {
              if (field === 'growth_method') {
                return { label: o.name, value: o.name };
              }
              return { label: o.name || o.label, value: o.name || o.value };
            }))}
          />
        );
      }
      if (isNumber) {
        const isPrice = ['actual_price', 'reference_price'].includes(field);
        return (
          <InputNumber size="small" style={{ width: '100%' }} autoFocus
            min={0}
            precision={isPrice ? 2 : 0}
            value={editingValue}
            onChange={v => { editingValueRef.current = v; setEditingValue(v); }}
            onPressEnter={() => handleSaveEdit(record.id, field)}
            onBlur={() => handleSaveEdit(record.id, field)}
            onKeyDown={e => { if (e.key === 'Escape') handleCancelEdit(); }}
          />
        );
      }
      return (
        <Input size="small" style={{ width: '100%' }} autoFocus
          value={editingValue}
          onChange={e => { editingValueRef.current = e.target.value; setEditingValue(e.target.value); }}
          onPressEnter={() => handleSaveEdit(record.id, field)}
          onBlur={() => handleSaveEdit(record.id, field)}
          onKeyDown={e => { if (e.key === 'Escape') handleCancelEdit(); }}
        />
      );
    }
    const content = renderFn ? renderFn(record[field], record) : (record[field] || '-');
    return (
      <div onDoubleClick={() => handleCellDoubleClick(record, field)} style={{ cursor: 'pointer', minHeight: 22 }}>
        {content}
      </div>
    );
  };

  // 行操作按钮（表格和手机卡片共用）
  const renderOrderActions = (record) => (
    <Space size={0}>
      <Button type="link" size="small" onClick={() => navigate(`/orders/${record.id}`)}>查看</Button>
      {record.platform === '视频号' && isValidUrl(record.text3) && (
        <Button type="link" size="small" onClick={async () => {
          setAuthCodeRecord(record);
          setAuthCodeVisible(true);
          setAuthCodeLoading(true);
          try {
            const res = await api.get('/orders/resolve-url', { params: { url: record.text3.trim() } });
            setAuthCodeUrl(res.data?.resolved || record.text3.trim());
          } catch (err) {
            setAuthCodeUrl(record.text3.trim());
          }
          setAuthCodeLoading(false);
        }}>授权码</Button>
      )}
      {canEdit && enabledApiPlatforms.includes(record.platform) && (
        <Popconfirm title="查询粉丝数并更新最终粉丝？" onConfirm={async () => {
          try {
            const res = await api.post(`/orders/${record.id}/query-fans`);
            if (res.code === 200) {
              message.success(res.message);
              loadData();
            }
          } catch (err) {}
        }}>
          <Button type="link" size="small">更新</Button>
        </Popconfirm>
      )}
    </Space>
  );

  // 所有可用列定义
  const allColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: true },
    { title: '主体', dataIndex: 'supplier_name', key: 'supplier_name', width: 120, adminOnly: true, field: 'supplier_id' },
    { title: '平台', dataIndex: 'platform', key: 'platform', width: 80, sorter: true, field: 'platform',
      render: (v, record) => renderEditableCell('platform', record)
    },
    { title: '受理员工', dataIndex: 'handler', key: 'handler', width: 100, sorter: true, field: 'handler',
      render: (v, record) => renderEditableCell('handler', record)
    },
    { title: '小组', dataIndex: 'team', key: 'team', width: 80, sorter: true, field: 'team',
      render: (v, record) => renderEditableCell('team', record)
    },
    {
      title: '下单时间', dataIndex: 'order_time', key: 'order_time', width: 160, sorter: true, field: 'order_time',
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'
    },
    { title: '涨粉方式', dataIndex: 'growth_method', key: 'growth_method', width: 100, field: 'growth_method',
      render: (v, record) => renderEditableCell('growth_method', record)
    },
    {
      title: '账号名称', dataIndex: 'account_name', key: 'account_name', width: 120, ellipsis: true, field: 'account_name',
      render: (v, record) => {
        const dup = duplicateAccounts.get(v);
        const content = renderEditableCell('account_name', record);
        if (!dup) return content;
        const isPurple = dup.type === 'account_method' && dup.methods.has(record.growth_method || '');
        const color = isPurple ? '#722ed1' : '#ff4d4f';
        return <span style={{ color, fontWeight: 'bold' }}>{content}</span>;
      }
    },
    {
      title: '完结状态', dataIndex: 'status_name', key: 'status_name', width: 140,
      render: (v, record) => {
        const statusEditable = isFieldEditable('status_id', record);
        const restricted = isStatusRestricted(record);
        if (!statusEditable || restricted) {
          if (!v) return '-';
          return (
            <Tooltip title="该状态已锁定或受权限限制，无法修改">
              <Tag color={restricted ? 'red' : 'blue'}>{v}</Tag>
            </Tooltip>
          );
        }
        return (
          <Select
            size="small"
            style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={record.status_id || undefined}
            allowClear
            placeholder="选择状态"
            onChange={async (val) => {
              try {
                const res = await api.put(`/orders/${record.id}/status`, { status_id: val });
                if (res.code === 200) {
                  message.success('状态更新成功');
                  loadData();
                  loadFilterCounts();
                }
              } catch (err) {}
            }}
            options={withTitle(selectableStatuses.map(s => ({ label: s.name, value: s.id })))}
          />
        );
      }
    },
    {
      title: '备注', dataIndex: 'remark', key: 'remark', width: 120, ellipsis: true, field: 'remark',
      render: (v, record) => renderEditableCell('remark', record)
    },
    {
      title: '完成日期', dataIndex: 'complete_date', key: 'complete_date', width: 110, sorter: true, field: 'complete_date',
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '-'
    },
    {
      title: '最新修改时间', dataIndex: 'updated_at', key: 'updated_at', width: 160, sorter: true,
      render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '-'
    },
    { title: '最新修改人', dataIndex: 'modified_by_name', key: 'modified_by_name', width: 100, field: 'modified_by_name', render: v => v || '-' },
    { title: '初始粉丝', dataIndex: 'initial_fans', key: 'initial_fans', width: 90, sorter: true, align: 'right', field: 'initial_fans',
      render: (v, record) => renderEditableCell('initial_fans', record)
    },
    { title: '最终粉丝', dataIndex: 'final_fans', key: 'final_fans', width: 90, sorter: true, align: 'right', field: 'final_fans',
      render: (v, record) => renderEditableCell('final_fans', record)
    },
    { title: '最终有效粉', dataIndex: 'effective_fans', key: 'effective_fans', width: 100, sorter: true, align: 'right', field: 'effective_fans',
      render: (v, record) => renderEditableCell('effective_fans', record, (val) => (val === null || val === undefined || val === '') ? '0' : val)
    },
    { title: '涨粉量', dataIndex: 'growth_count', key: 'growth_count', width: 80, sorter: true, align: 'right', field: 'growth_count' },
    { title: '期数', dataIndex: 'period', key: 'period', width: 80, sorter: true, field: 'period',
      render: (v, record) => renderEditableCell('period', record)
    },
    { title: '群名', dataIndex: 'group_name', key: 'group_name', width: 120, field: 'group_name',
      render: (v, record) => renderEditableCell('group_name', record)
    },
    { title: '对接人', dataIndex: 'contact_person', key: 'contact_person', width: 80, field: 'contact_person',
      render: (v, record) => renderEditableCell('contact_person', record)
    },
    {
      title: '参考价格', dataIndex: 'reference_price', key: 'reference_price', width: 90, sorter: true, align: 'right', field: 'reference_price',
      render: (v, record) => renderEditableCell('reference_price', record, (val) => val ? `¥${val}` : '-')
    },
    {
      title: '实际价格', dataIndex: 'actual_price', key: 'actual_price', width: 90, sorter: true, align: 'right', field: 'actual_price',
      render: (v, record) => renderEditableCell('actual_price', record, (val) => val ? `¥${val}` : '-')
    },
    {
      title: '文本1', dataIndex: 'text1', key: 'text1', width: 100, ellipsis: true, field: 'text1',
      render: (v, record) => renderEditableCell('text1', record)
    },
    {
      title: '文本2', dataIndex: 'text2', key: 'text2', width: 100, ellipsis: true, field: 'text2',
      render: (v, record) => renderEditableCell('text2', record)
    },
    {
      title: '文本3', dataIndex: 'text3', key: 'text3', width: 100, ellipsis: true, field: 'text3',
      render: (v, record) => renderEditableCell('text3', record)
    },
    {
      title: '操作', key: 'action', width: 160, fixed: 'right',
      render: (_, record) => renderOrderActions(record)
    }
  ];

  // 根据配置过滤和排序列
  const getColumns = () => {
    let filtered = allColumns.filter(col => {
      // 操作列始终显示
      if (col.key === 'action') return true;
      // ID列始终显示
      if (col.key === 'id') return true;
      // 主体列仅管理员可见
      if (col.adminOnly && !isAdmin) return false;
      // 完结状态列始终显示
      if (col.key === 'status_name') return true;
      // 其他列根据字段权限
      if (col.field && !isFieldVisible(col.field)) return false;
      return true;
    });

    // 如果有列配置，按配置排序
    if (columnOrder.length > 0) {
      const orderMap = {};
      columnOrder.forEach((key, index) => { orderMap[key] = index; });
      filtered.sort((a, b) => {
        const aOrder = orderMap[a.key] ?? 999;
        const bOrder = orderMap[b.key] ?? 999;
        return aOrder - bOrder;
      });
    }

    return filtered;
  };

  // 列配置弹窗
  const ColumnConfigModal = () => {
    const [tempOrder, setTempOrder] = useState([]);
    const [dragItem, setDragItem] = useState(null);

    useEffect(() => {
      if (columnConfigVisible) {
        // 合并已保存的列顺序和所有列（新增列追加到末尾）
        const allKeys = allColumns.map(c => c.key);
        if (columnOrder.length > 0) {
          const saved = columnOrder.filter(k => allKeys.includes(k));
          const newKeys = allKeys.filter(k => !columnOrder.includes(k));
          setTempOrder([...saved, ...newKeys]);
        } else {
          setTempOrder(allKeys);
        }
      }
    }, [columnConfigVisible]);

    const handleDragStart = (index) => {
      setDragItem(index);
    };

    const handleDragOver = (e, index) => {
      e.preventDefault();
      if (dragItem === null) return;
      const newOrder = [...tempOrder];
      const item = newOrder.splice(dragItem, 1)[0];
      newOrder.splice(index, 0, item);
      setDragItem(index);
      setTempOrder(newOrder);
    };

    const handleDragEnd = () => {
      setDragItem(null);
    };

    const handleSave = () => {
      saveColumnConfig(tempOrder);
      setColumnConfigVisible(false);
      message.success('列配置已保存');
    };

    return (
      <Modal
        title="列配置（拖拽排序）"
        open={columnConfigVisible}
        onOk={handleSave}
        onCancel={() => setColumnConfigVisible(false)}
        width={400}
      >
        <div style={{ maxHeight: 400, overflow: 'auto' }}>
          {tempOrder.map((key, index) => {
            const col = allColumns.find(c => c.key === key);
            if (!col || col.key === 'action') return null;
            return (
              <div
                key={key}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                style={{
                  padding: '8px 12px',
                  marginBottom: 4,
                  background: dragItem === index ? '#e6f7ff' : '#fafafa',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  cursor: 'move',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}
              >
                <ColumnHeightOutlined style={{ color: '#999' }} />
                <span>{col.title}</span>
              </div>
            );
          })}
        </div>
      </Modal>
    );
  };

  return (
    <div>
      <style>{`
        .duplicate-row td { background: #fff1f0 !important; }
        .duplicate-row-purple td { background: #f9f0ff !important; }
        /* 下拉选项完整展示文案，不换行不省略 */
        .ant-select-dropdown .ant-select-item-option-content { white-space: nowrap; }
        .ant-select-dropdown { max-width: 560px; }
      `}</style>
      {/* 筛选区域 + 操作栏（整体固定） */}
      <div style={{ position: 'static', background: '#fff', paddingTop: isMobile ? 8 : 12, paddingBottom: 4, borderBottom: '1px solid #f0f0f0', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: isMobile ? 8 : 12 }}>
          <Radio.Group value={searchMode} onChange={e => setSearchMode(e.target.value)} buttonStyle="solid" size="small">
            <Radio.Button value="simple">简单搜索</Radio.Button>
            <Radio.Button value="batch">批量搜索</Radio.Button>
          </Radio.Group>
          {isMobile && (
            <Button size="small" icon={<FilterOutlined />} onClick={() => setFilterExpanded(v => !v)}>
              {filterExpanded ? '收起筛选' : '展开筛选'}
            </Button>
          )}
        </div>

        {(!isMobile || filterExpanded) && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))', gap: isMobile ? 8 : '10px 12px', marginBottom: isMobile ? 8 : 12 }}>
          {isAdmin && isFieldVisible('supplier_id') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>主体</div>
            <Select
              mode="multiple"
              placeholder="选择主体"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.supplier_id}
              onChange={v => setFilters(prev => ({ ...prev, supplier_id: v }))}
              allowClear
              maxTagCount={1}
              options={withTitle([
                { label: `空（${filterCounts.suppliers.find(c => c.id === null)?.count || 0}）`, value: '__blank__' },
                ...suppliers.map(s => {
                  const cnt = filterCounts.suppliers.find(c => c.id === s.id);
                  return { label: `${s.name}（${cnt ? cnt.count : 0}）`, value: s.id };
                })
              ])}
            />
            </div>
          )}
          {isFieldVisible('status_id') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>完结状态</div>
            <Select
              mode="multiple"
              placeholder="选择状态"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.status_id}
              onChange={v => setFilters(prev => ({ ...prev, status_id: v }))}
              allowClear
              maxTagCount={1}
              options={withTitle([
                { label: `空（${filterCounts.statuses.find(c => c.id === null)?.count || 0}）`, value: '__blank__' },
                ...visibleStatuses.map(s => {
                  const cnt = filterCounts.statuses.find(c => c.id === s.id);
                  return { label: `${s.name}（${cnt ? cnt.count : 0}）`, value: s.id };
                })
              ])}
            />
            </div>
          )}
          {isFieldVisible('platform') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>平台</div>
            <Select
              placeholder="选择平台"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.platform}
              onChange={v => setFilters(prev => ({ ...prev, platform: v }))}
              allowClear
              options={withTitle([
                { label: `空（${(filterCounts.platforms || []).find(c => !c.name)?.count || 0}）`, value: '__blank__' },
                ...platforms.map(p => {
                  const cnt = (filterCounts.platforms || []).find(c => c.name === p.name);
                  return { label: `${p.name}（${cnt ? cnt.count : 0}）`, value: p.name };
                })
              ])}
            />
            </div>
          )}
          {isFieldVisible('effective_fans') && (
            <RangeFilter
              label="最终有效粉"
              presets={RANGE_PRESETS}
              preset={effectiveFansPreset}
              min={filters.effective_fans_min}
              max={filters.effective_fans_max}
              size={isMobile ? 'small' : 'middle'}
              onChange={({ preset, min, max }) => {
                setEffectiveFansPreset(preset);
                setPage(1);
                setFilters(prev => ({ ...prev, effective_fans_min: min, effective_fans_max: max }));
              }}
            />
          )}
          {isFieldVisible('initial_fans') && (
            <RangeFilter
              label="初始粉丝"
              presets={RANGE_PRESETS}
              preset={initialFansPreset}
              min={filters.initial_fans_min}
              max={filters.initial_fans_max}
              size={isMobile ? 'small' : 'middle'}
              onChange={({ preset, min, max }) => {
                setInitialFansPreset(preset);
                setPage(1);
                setFilters(prev => ({ ...prev, initial_fans_min: min, initial_fans_max: max }));
              }}
            />
          )}
          {isFieldVisible('final_fans') && (
            <RangeFilter
              label="最终粉丝"
              presets={RANGE_PRESETS}
              preset={finalFansPreset}
              min={filters.final_fans_min}
              max={filters.final_fans_max}
              size={isMobile ? 'small' : 'middle'}
              onChange={({ preset, min, max }) => {
                setFinalFansPreset(preset);
                setPage(1);
                setFilters(prev => ({ ...prev, final_fans_min: min, final_fans_max: max }));
              }}
            />
          )}
          {isFieldVisible('growth_count') && (
            <RangeFilter
              label="涨粉量"
              presets={GROWTH_PRESETS}
              preset={growthCountPreset}
              min={filters.growth_count_min}
              max={filters.growth_count_max}
              size={isMobile ? 'small' : 'middle'}
              onChange={({ preset, min, max }) => {
                setGrowthCountPreset(preset);
                setPage(1);
                setFilters(prev => ({ ...prev, growth_count_min: min, growth_count_max: max }));
              }}
            />
          )}
          {isFieldVisible('growth_method') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>涨粉方式</div>
            <Select
              mode="multiple"
              placeholder="选择涨粉方式"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.growth_method}
              onChange={v => setFilters(prev => ({ ...prev, growth_method: v }))}
              allowClear
              maxTagCount={1}
              options={withTitle([
                { label: `空（${filterCounts.growthMethods.find(c => c.name === null || c.name === '')?.count || 0}）`, value: '__blank__' },
                ...growthMethods.map(g => {
                  const cnt = filterCounts.growthMethods.find(c => c.name === g.name);
                  return { label: `${g.name}（${cnt ? cnt.count : 0}）`, value: g.name };
                })
              ])}
            />
            </div>
          )}
          {isFieldVisible('team') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>小组</div>
            <Select
              mode="multiple"
              placeholder="选择小组"
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              value={filters.team}
              onChange={v => setFilters(prev => ({ ...prev, team: v }))}
              allowClear
              maxTagCount={1}
              options={withTitle([
                { label: `空（${filterCounts.teams.find(c => c.name === null || c.name === '')?.count || 0}）`, value: '__blank__' },
                ...teams.map(t => {
                  const cnt = filterCounts.teams.find(c => c.name === t.name);
                  return { label: `${t.name}（${cnt ? cnt.count : 0}）`, value: t.name };
                })
              ])}
            />
            </div>
          )}
          {isFieldVisible('handler') && (
            <div>
              <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>受理员工</div>
            <Input
              placeholder="员工"
              style={{ width: '100%' }}
              value={filters.handler}
              onChange={e => setFilters(prev => ({ ...prev, handler: e.target.value || undefined }))}
              allowClear
            />
            </div>
          )}
          <div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>下单时间</div>
            <RangePicker
              value={filters.dateRange}
              onChange={v => setFilters(prev => ({ ...prev, dateRange: v }))}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>完成日期</div>
            <RangePicker
              value={filters.completeDateRange}
              onChange={v => setFilters(prev => ({ ...prev, completeDateRange: v }))}
              placeholder={['完成日期起', '完成日期止']}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>最新修改时间</div>
            <RangePicker
              value={filters.updatedDateRange}
              onChange={v => setFilters(prev => ({ ...prev, updatedDateRange: v }))}
              placeholder={['修改时间起', '修改时间止']}
              style={{ width: '100%' }}
            />
          </div>
        </div>
        )}

        {searchMode === 'simple' ? (
          <div style={{ display: 'flex', gap: isMobile ? 6 : 12, marginBottom: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
            <Select
              popupMatchSelectWidth={false}
              value={filters.search_field}
              onChange={v => setFilters(prev => ({ ...prev, search_field: v }))}
              style={isMobile ? { width: 100 } : { width: 120 }}
              options={SEARCHABLE_FIELDS}
              size={isMobile ? 'small' : 'middle'}
            />
            <Input
              placeholder={isMobile ? '搜索' : (filters.search_field === 'all' ? '搜索全部字段' : `搜索${SEARCHABLE_FIELDS.find(f => f.value === filters.search_field)?.label || ''}`)}
              prefix={<SearchOutlined />}
              style={{ flex: 1, minWidth: isMobile ? 100 : 300 }}
              value={filters.keyword}
              onChange={e => setFilters(prev => ({ ...prev, keyword: e.target.value }))}
              onPressEnter={loadData}
              allowClear
              size={isMobile ? 'small' : 'middle'}
            />
            <Button icon={<SearchOutlined />} type="primary" onClick={loadData} size={isMobile ? 'small' : 'middle'}>搜索</Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: isMobile ? 6 : 12, alignItems: 'flex-start', marginBottom: isMobile ? 8 : 12, flexWrap: 'wrap' }}>
            <Select
              popupMatchSelectWidth={false}
              value={filters.search_field}
              onChange={v => setFilters(prev => ({ ...prev, search_field: v }))}
              style={isMobile ? { width: 100 } : { width: 120 }}
              options={SEARCHABLE_FIELDS}
              size={isMobile ? 'small' : 'middle'}
            />
            <Select
              popupMatchSelectWidth={false}
              value={filters.match_type}
              onChange={v => setFilters(prev => ({ ...prev, match_type: v }))}
              style={isMobile ? { width: 80 } : { width: 100 }}
              options={[
                { label: '模糊', value: 'fuzzy' },
                { label: '精准', value: 'exact' }
              ]}
              size={isMobile ? 'small' : 'middle'}
            />
            <TextArea
              placeholder="一行一个关键词"
              style={{ flex: 1, minWidth: isMobile ? 120 : 300 }}
              rows={isMobile ? 2 : 3}
              value={filters.keywords}
              onChange={e => setFilters(prev => ({ ...prev, keywords: e.target.value }))}
            />
            <Button icon={<SearchOutlined />} type="primary" onClick={loadData} size={isMobile ? 'small' : 'middle'}>搜索</Button>
          </div>
        )}

        {/* 批量操作栏 */}
        {(isAdmin || user?.role === 'supplier') && (
          <div style={{ display: 'flex', gap: isMobile ? 6 : 12, flexWrap: 'wrap', paddingBottom: 4 }}>
            {isAdmin && canAdd && (
              <Button icon={<PlusOutlined />} type="primary" onClick={() => navigate('/orders/new')} size={isMobile ? 'small' : 'middle'}>
                {isMobile ? '新增' : '新增订单'}
              </Button>
            )}
            {canImport && canImportMenu && (
              <Button onClick={() => navigate('/orders/import')} size={isMobile ? 'small' : 'middle'}>导入</Button>
            )}
            {canQuickAdd && canImportMenu && (
              <Button icon={<ThunderboltOutlined />} onClick={() => { setQuickImportTeam(undefined); setQuickImportVisible(true); }} size={isMobile ? 'small' : 'middle'}>
                快速导入
              </Button>
            )}
            {(isSuperAdmin || ((canImport || canQuickAdd) && canImportMenu)) && (
              <Button icon={<SearchOutlined />} onClick={() => setCheckDupVisible(true)} size={isMobile ? 'small' : 'middle'}>
                查重
              </Button>
            )}
            {selectedRowKeys.length > 0 && canBatchEdit && isFieldEditable('status_id') && (
              <Dropdown
                menu={{
                  items: selectableStatuses.map(s => ({
                    key: s.id,
                    label: s.name,
                    onClick: () => handleBatchStatus(s.id)
                  }))
                }}
              >
                <Button icon={<SwapOutlined />} size={isMobile ? 'small' : 'middle'}>
                  改状态({selectedRowKeys.length})
                </Button>
              </Dropdown>
            )}
            {selectedRowKeys.length > 0 && canBatchEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => setBatchEditVisible(true)}
                size={isMobile ? 'small' : 'middle'}
              >
                批量修改({selectedRowKeys.length})
              </Button>
            )}
            {selectedRowKeys.length > 0 && canBatchEdit && (
              <Button
                icon={<EditOutlined />}
                onClick={() => { setBatchFansField('initial_fans'); setBatchFansText(''); setBatchFansVisible(true); }}
                size={isMobile ? 'small' : 'middle'}
              >
                批量改粉丝({selectedRowKeys.length})
              </Button>
            )}
            {selectedRowKeys.length > 0 && canBatchAssign && canEdit && (
              <Dropdown
                menu={{
                  items: suppliers.filter(s => s.status === 1).map(s => ({
                    key: s.id,
                    label: s.name,
                    onClick: () => handleBatchAssign(s.id)
                  }))
                }}
              >
                <Button icon={<FilterOutlined />} size={isMobile ? 'small' : 'middle'}>
                  分配({selectedRowKeys.length})
                </Button>
              </Dropdown>
            )}
            {selectedRowKeys.length > 0 && (
              <Tooltip title="复制选中订单的抖音号到剪切板">
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleBatchCopy}
                  size={isMobile ? 'small' : 'middle'}
                >
                  复制抖音号
                </Button>
              </Tooltip>
            )}
            {selectedRowKeys.length > 0 && canDelete && (
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={handleBatchDelete}
                size={isMobile ? 'small' : 'middle'}
              >
                删除({selectedRowKeys.length})
              </Button>
            )}
            <Dropdown
              menu={{
                items: [
                  { key: 'filtered', label: '导出筛选数据', onClick: () => handleExport('filtered') },
                  { key: 'selected', label: '导出选中数据', onClick: () => handleExport('selected'), disabled: selectedRowKeys.length === 0 },
                  { type: 'divider' },
                  { key: 'all', label: '导出全部数据', onClick: () => handleExport('all') }
                ]
              }}
            >
              <Button icon={<DownloadOutlined />} size={isMobile ? 'small' : 'middle'}>导出</Button>
            </Dropdown>
            <Button
              icon={<SettingOutlined />}
              onClick={() => setColumnConfigVisible(true)}
              size={isMobile ? 'small' : 'middle'}
            >
              列配置
            </Button>
          </div>
        )}
      </div>

      {/* 数据表格 */}
      {isMobile ? (
        <Spin spinning={loading}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Checkbox
              checked={data.length > 0 && data.every(r => selectedRowKeys.includes(r.id))}
              indeterminate={data.some(r => selectedRowKeys.includes(r.id)) && !data.every(r => selectedRowKeys.includes(r.id))}
              onChange={e => {
                const ids = data.map(r => r.id);
                setSelectedRowKeys(e.target.checked
                  ? Array.from(new Set([...selectedRowKeys, ...ids]))
                  : selectedRowKeys.filter(id => !ids.includes(id)));
              }}
            >全选本页</Checkbox>
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>共 {total} 条</span>
          </div>

          {data.map(record => {
            const dup = duplicateAccounts.get(record.account_name);
            const locked = record.status_is_locked === 1;
            const restricted = isStatusRestricted(record);
            return (
              <div key={record.id} style={{
                border: '1px solid #f0f0f0',
                borderRadius: 10,
                padding: 12,
                marginBottom: 10,
                background: dup ? '#fff1f0' : '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <Checkbox
                    checked={selectedRowKeys.includes(record.id)}
                    onChange={e => {
                      setSelectedRowKeys(e.target.checked
                        ? [...selectedRowKeys, record.id]
                        : selectedRowKeys.filter(id => id !== record.id));
                    }}
                  >
                    <span style={{ fontWeight: 600, color: dup ? '#cf1322' : '#1f1f1f' }}>{record.account_name || '-'}</span>
                  </Checkbox>
                  {record.status_name
                    ? <Tag color={restricted ? 'red' : (locked ? 'red' : 'blue')}>{record.status_name}</Tag>
                    : <span style={{ fontSize: 12, color: '#bfbfbf' }}>未设置状态</span>}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', fontSize: 13, color: '#595959' }}>
                  {isAdmin && <div>主体：{record.supplier_name || '-'}</div>}
                  <div>平台：{record.platform || '-'}</div>
                  <div>小组：{record.team || '-'}</div>
                  <div>涨粉方式：{record.growth_method || '-'}</div>
                  <div>下单时间：{record.order_time ? dayjs(record.order_time).format('MM-DD HH:mm') : '-'}</div>
                  <div>涨粉量：{record.growth_count ?? '-'}</div>
                  {isFieldVisible('effective_fans') && <div>最终有效粉：{record.effective_fans ?? 0}</div>}
                  {isFieldVisible('handler') && <div>受理员工：{record.handler || '-'}</div>}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f5f5f5', marginTop: 8, paddingTop: 4 }}>
                  {renderOrderActions(record)}
                </div>
              </div>
            );
          })}

          {data.length === 0 && !loading && (
            <div style={{ textAlign: 'center', color: '#8c8c8c', padding: '40px 0' }}>暂无订单</div>
          )}

          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <Pagination
              simple
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={(p) => setPage(p)}
            />
          </div>
        </Spin>
      ) : (
      <Table
        rowKey="id"
        columns={getColumns()}
        dataSource={data}
        loading={loading}
        scroll={{ x: 1500 }}
        sticky={{ offsetHeader: 64 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: total => `共 ${total} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          pageSizeOptions: ['20', '50', '100', '200', '500']
        }}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
          },
          fixed: true
        }}
        onChange={handleTableChange}
        size="small"
        rowClassName={record => {
          const dup = duplicateAccounts.get(record.account_name);
          if (!dup) return '';
          if (dup.type === 'account_method' && dup.methods.has(record.growth_method || '')) return 'duplicate-row-purple';
          return 'duplicate-row';
        }}
      />
      )}

      {/* 快速导入弹窗 */}
      <Modal
        title="快速导入（批量输入抖音号）"
        open={quickImportVisible}
        onOk={handleQuickImport}
        onCancel={() => { setQuickImportVisible(false); setQuickImportText(''); setQuickImportTeam(undefined); }}
        confirmLoading={quickImporting}
        width={500}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <Select allowClear showSearch placeholder="涨粉方式" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={quickImportGrowthMethod} onChange={v => setQuickImportGrowthMethod(v)}
            options={withTitle(growthMethods.map(g => ({ label: g.name, value: g.name })))}
            optionFilterProp="label"
          />
          <Select allowClear showSearch placeholder="平台" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={quickImportPlatform} onChange={v => setQuickImportPlatform(v)}
            options={withTitle(platforms.map(p => ({ label: p.name, value: p.name })))}
            optionFilterProp="label"
          />
          <Select allowClear showSearch placeholder="主体" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={quickImportSupplier} onChange={v => setQuickImportSupplier(v)}
            options={withTitle(suppliers.map(s => ({ label: s.name, value: s.id })))}
            optionFilterProp="label"
          />
          <Select allowClear showSearch placeholder="小组" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={quickImportTeam} onChange={v => setQuickImportTeam(v)}
            options={withTitle(teams.map(t => ({ label: t.name, value: t.name })))}
            optionFilterProp="label"
          />
          <Select allowClear showSearch placeholder="完结状态" style={{ width: '100%' }}
            popupMatchSelectWidth={false}
            value={quickImportStatus} onChange={v => setQuickImportStatus(v)}
            options={withTitle(selectableStatuses.map(s => ({ label: s.name, value: s.id })))}
            optionFilterProp="label"
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <span style={{ marginRight: 12 }}>查重方式：</span>
          <Radio.Group value={quickImportCheckType} onChange={e => setQuickImportCheckType(e.target.value)}>
            <Radio value="account">按账号查重</Radio>
            <Radio value="account_method">按账号+涨粉方式查重</Radio>
          </Radio.Group>
        </div>
        <p style={{ marginBottom: 8, color: '#666' }}>一行一个抖音号，系统会自动创建订单：</p>
        <TextArea
          placeholder="请输入抖音号，一行一个&#10;例如：&#10;抖音号1&#10;抖音号2&#10;抖音号3"
          rows={10}
          value={quickImportText}
          onChange={e => setQuickImportText(e.target.value)}
        />
      </Modal>

      {/* 批量修改弹窗 */}
      <Modal
        title={`批量修改（${selectedRowKeys.length} 条订单）`}
        open={batchEditVisible}
        onOk={handleBatchEdit}
        onCancel={() => { setBatchEditVisible(false); setBatchEditField(null); setBatchEditValue(null); }}
        confirmLoading={batchEditing}
        width={500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ marginBottom: 8 }}>选择要修改的字段：</div>
            <Select
              popupMatchSelectWidth={false}
              style={{ width: '100%' }}
              placeholder="请选择字段"
              value={batchEditField}
              onChange={(v) => { setBatchEditField(v); setBatchEditValue(null); }}
              options={withTitle(getBatchEditableFields().map(f => ({ label: f.label, value: f.value })))}
            />
          </div>
          {batchEditField && (
            <div>
              <div style={{ marginBottom: 8 }}>设置新值：</div>
              {renderBatchEditInput()}
            </div>
          )}
        </div>
      </Modal>

      {/* 批量修改粉丝弹窗 */}
      <Modal
        title={`批量修改粉丝（${selectedRowKeys.length} 条订单）`}
        open={batchFansVisible}
        onOk={handleBatchFans}
        onCancel={() => { setBatchFansVisible(false); setBatchFansText(''); }}
        confirmLoading={batchFansLoading}
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>选择修改字段：</div>
          <Radio.Group value={batchFansField} onChange={e => { setBatchFansField(e.target.value); setBatchFansText(''); }}>
            <Radio.Button value="initial_fans">初始粉丝</Radio.Button>
            <Radio.Button value="final_fans">最终粉丝</Radio.Button>
            <Radio.Button value="effective_fans">最终有效粉</Radio.Button>
          </Radio.Group>
        </div>
        <p style={{ marginBottom: 8, color: '#666' }}>
          每行一个数字，按选中订单顺序一一对应
        </p>
        <p style={{ marginBottom: 12, color: '#999', fontSize: 12 }}>
          第1行 → 第1条选中订单，第2行 → 第2条...<br/>
          行数不够则后面的不修改，行数超出则忽略。<br/>
          涨粉量会自动计算 = 最终粉丝 - 初始粉丝
        </p>
        <Input.TextArea
          rows={12}
          placeholder={`请输入粉丝数据，一行一个：\n100\n200\n50`}
          value={batchFansText}
          onChange={e => setBatchFansText(e.target.value)}
        />
      </Modal>

      {/* 列配置弹窗 */}
      <ColumnConfigModal />

      {/* 授权码弹窗 */}
      <Modal
        title="授权码"
        open={authCodeVisible}
        onCancel={() => { setAuthCodeVisible(false); setAuthCodeRecord(null); setAuthCodeUrl(''); }}
        footer={null}
        width={500}
      >
        {authCodeRecord && (
          <div style={{ textAlign: 'center' }}>
            {authCodeLoading ? (
              <div style={{ padding: 40 }}>加载中...</div>
            ) : (
              <img src={authCodeUrl || authCodeRecord.text3.trim()} alt="授权码" style={{ maxWidth: '100%', maxHeight: 400, marginBottom: 16 }} />
            )}
            <div style={{ textAlign: 'left', fontSize: 14, color: '#666', lineHeight: 2 }}>
              <div><strong>账号：</strong>{authCodeRecord.account_name || '-'}</div>
              {authCodeRecord.team && <div><strong>小组：</strong>{authCodeRecord.team}</div>}
              {authCodeRecord.contact_person && <div><strong>对接人：</strong>{authCodeRecord.contact_person}</div>}
              {authCodeRecord.group_name && <div><strong>群名：</strong>{authCodeRecord.group_name}</div>}
              {authCodeRecord.period && <div><strong>期数：</strong>{authCodeRecord.period}</div>}
            </div>
          </div>
        )}
      </Modal>

      {/* 查重弹窗 */}
      <Modal
        title="查重结果"
        open={duplicateVisible}
        onCancel={() => { setDuplicateVisible(false); setPendingAccounts([]); }}
        footer={pendingAccounts.length > 0 ? [
          <Button key="cancel" onClick={() => { setDuplicateVisible(false); setPendingAccounts([]); }}>取消</Button>,
          <Button key="dedup" onClick={handleDedupImport} loading={quickImporting}>去除重复后添加</Button>,
          <Button key="force" type="primary" onClick={handleForceImport} loading={quickImporting}>全部添加（含重复）</Button>
        ] : null}
        width={600}
      >
        {duplicateData.length === 0 ? (
          <p style={{ color: '#52c41a' }}>未发现重复数据</p>
        ) : (
          <div>
            <p style={{ color: '#ff4d4f', marginBottom: 12 }}>发现以下重复的抖音号：</p>
            <Table
              size="small"
              pagination={false}
              dataSource={duplicateData.map((d, i) => ({ ...d, _key: i }))}
              rowKey="_key"
              columns={[
                { title: '抖音号', dataIndex: 'account_name' },
                { title: '涨粉方式', dataIndex: 'growth_method' },
                { title: '主体', dataIndex: 'supplier_name', render: v => v || '-' },
                { title: '小组', dataIndex: 'team', render: v => v || '-' },
                { title: '订单ID', dataIndex: 'id', width: 80 }
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 独立查重弹窗 */}
      <Modal
        title="抖音号查重"
        open={checkDupVisible}
        onOk={handleStandaloneCheckDup}
        onCancel={() => { setCheckDupVisible(false); setCheckDupText(''); setDuplicateData([]); }}
        confirmLoading={checkDupLoading}
        okText="开始查重"
        width={600}
      >
        <div style={{ marginBottom: 12 }}>
          <span style={{ marginRight: 12 }}>查重方式：</span>
          <Radio.Group value={checkDupType} onChange={e => { setCheckDupType(e.target.value); setCheckDupText(''); setDuplicateData([]); }}>
            <Radio value="account">按账号查重</Radio>
            <Radio value="account_method">按账号+涨粉方式查重</Radio>
          </Radio.Group>
        </div>
        <p style={{ marginBottom: 8, color: '#666' }}>
          {checkDupType === 'account' ? '一行一个抖音号：' : '一行一条，格式：抖音号|||涨粉方式'}
        </p>
        <TextArea
          placeholder={checkDupType === 'account'
            ? "请输入抖音号，一行一个\n例如：\n抖音号1\n抖音号2\n抖音号3"
            : "请输入账号和涨粉方式，一行一条\n格式：抖音号|||涨粉方式\n例如：\n抖音号1|||抖音抖+\n抖音号2|||抖音自热"
          }
          rows={8}
          value={checkDupText}
          onChange={e => setCheckDupText(e.target.value)}
        />
        {duplicateData.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <p style={{ color: '#ff4d4f', marginBottom: 8 }}>发现以下重复数据：</p>
            <Table
              size="small"
              pagination={false}
              dataSource={duplicateData.map((d, i) => ({ ...d, _key: i }))}
              rowKey="_key"
              columns={[
                { title: '抖音号', dataIndex: 'account_name' },
                { title: '涨粉方式', dataIndex: 'growth_method' },
                { title: '主体', dataIndex: 'supplier_name', render: v => v || '-' },
                { title: '小组', dataIndex: 'team', render: v => v || '-' },
                { title: '订单ID', dataIndex: 'id', width: 80 }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OrderList;
